import { useState, useEffect, useCallback, useRef } from 'react';

const EXIT_INTENT_STORAGE_KEY = 'dc_exit_intent_dismissed';
const DISMISS_DURATION_DAYS = 7;
const SCROLL_THRESHOLD = 300;

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const hasFiredRef = useRef(false);

  const shouldShow = useCallback(() => {
    if (hasFiredRef.current) return false;

    const dismissed = localStorage.getItem(EXIT_INTENT_STORAGE_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DURATION_DAYS) return false;
    }

    return true;
  }, []);

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (!shouldShow()) return;
      if (e.clientY <= 0 && !hasFiredRef.current) {
        hasFiredRef.current = true;
        setIsVisible(true);
      }
    };

    let scrollTimer: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (!shouldShow()) return;
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        if (scrollPercent >= 40 && !hasFiredRef.current) {
          hasFiredRef.current = true;
          setIsVisible(true);
        }
      }, 200);
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [shouldShow]);

  const dismiss = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(EXIT_INTENT_STORAGE_KEY, String(Date.now()));
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      dismiss();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => dismiss(), 2000);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d98fjgi4irb0s7p1v6b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // keep raw responseText
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => dismiss(), 2500);
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          'Ocorreu um erro. Tente novamente.';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Erro de conexão. Verifique sua internet e tente novamente.');
      setFormStatus('error');
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Oferta especial"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />

      {/* Card */}
      <div className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden animate-scale-in z-10">
        {/* Close button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors z-20 cursor-pointer"
          aria-label="Fechar"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Header with gradient accent */}
        <div className="bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-600 px-6 py-8 text-center text-white">
          <div className="w-14 h-14 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-gift-2-line text-3xl"></i>
          </div>
          <h2
            className="text-2xl font-bold mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Espere! Uma oferta especial para você
          </h2>
          <p className="text-white/90 text-sm leading-relaxed">
            Antes de ir, receba uma <strong>avaliação gratuita</strong> do seu imóvel e acesso antecipado aos melhores lançamentos em Brasília.
          </p>
        </div>

        {/* Form */}
        <div className="px-6 py-6">
          {formStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Recebemos seu contato!</h3>
              <p className="text-gray-600 text-sm">
                Em breve um especialista entrará em contato com você.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4"
            >
              {/* Honeypot */}
              <div className="contact-phone_alt-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div>
                <label htmlFor="ei-name" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Seu nome
                </label>
                <input
                  id="ei-name"
                  name="name"
                  type="text"
                  required
                  placeholder="Digite seu nome completo"
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400"
                />
              </div>

              <div>
                <label htmlFor="ei-phone" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  WhatsApp
                </label>
                <input
                  id="ei-phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="(61) 9 9999-9999"
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400"
                />
              </div>

              <div>
                <label htmlFor="ei-email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  E-mail
                </label>
                <input
                  id="ei-email"
                  name="email"
                  type="email"
                  required
                  placeholder="seu@email.com"
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400"
                />
              </div>

              <div>
                <label htmlFor="ei-interest" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Tenho interesse em
                </label>
                <select
                  id="ei-interest"
                  name="interest"
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all bg-white text-gray-700 cursor-pointer"
                >
                  <option value="">Selecione uma opção</option>
                  <option value="comprar">Comprar imóvel</option>
                  <option value="vender">Vender imóvel</option>
                  <option value="alugar">Alugar imóvel</option>
                  <option value="avaliar">Avaliar meu imóvel</option>
                  <option value="investir">Investir em imóveis</option>
                </select>
              </div>

              {formStatus === 'error' && formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                  <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                  <span>{formError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-teal-600 text-white py-3.5 rounded-lg font-bold hover:bg-teal-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-sm whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    Quero receber a oferta
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                Seus dados estão seguros e não serão compartilhados.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.4s cubic-bezier(.22,1,.36,1);
        }
      `}</style>
    </div>
  );
};

export default ExitIntentPopup;